import posthog from 'posthog-js';

import { normalizeApiPathForSpanName } from 'src/utils/apiSpanName';

// TEMPORARY: remove once the endpoint behind the stackless "Object captured as
// exception with keys: error, isUnhandledError, meta" PostHog issue is known.
// The guard swallows these rejections before any other listener sees them, so
// this is the only place that can say which endpoint produced one.
export const LIFECYCLE_REJECTION_EVENT = 'query_lifecycle_rejection_swallowed';

// Distinct source/endpoint/status combinations, not a count of occurrences: a
// tab that loops for days must not spend its budget on boot-time failures.
const MAX_DISTINCT_PER_PAGE_LOAD = 20;
const FLUSH_RETRY_MS = 2000;
const MAX_FLUSH_RETRIES = 10;
const MAX_KEYS = 8;

export type LifecycleRejectionSource = 'handler_catch' | 'window_listener';

type Diagnostic = {
  source: LifecycleRejectionSource;
  endpoint: string | null;
  http_status: number | null;
  error_keys: string[] | null;
  meta_keys: string[] | null;
  route: string;
  visibility: string;
};

const pending: Diagnostic[] = [];
const seen = new Set<string>();
// The window listener only has the rejection object. Tagging it where the
// endpoint is known lets that path name the endpoint too.
const endpointByRejection = new WeakMap<object, string>();
let retries = 0;
let retryTimer: ReturnType<typeof setTimeout> | undefined;

export const tagLifecycleRejection = (
  reason: unknown,
  endpoint: string,
): void => {
  if (typeof reason === 'object' && reason !== null) {
    endpointByRejection.set(reason, endpoint);
  }
};

const keysOf = (value: unknown): string[] | null =>
  typeof value === 'object' && value !== null
    ? Object.keys(value).slice(0, MAX_KEYS)
    : null;

const statusOf = (error: unknown): number | null => {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status : null;
};

const flush = () => {
  retryTimer = undefined;
  if (!pending.length) {
    return;
  }
  if (!posthog.__loaded) {
    // Boot-time rejections land before PostHogProvider initializes.
    if (retries++ < MAX_FLUSH_RETRIES) {
      retryTimer = setTimeout(flush, FLUSH_RETRY_MS);
    }
    return;
  }
  pending
    .splice(0)
    .forEach((entry) => posthog.capture(LIFECYCLE_REJECTION_EVENT, entry));
};

// Keys, status and endpoint name only. The rejection's `meta` carries the
// request and response, which can hold URLs, tokens and user data.
export const recordSwallowedLifecycleRejection = (
  source: LifecycleRejectionSource,
  reason: unknown,
  endpoint?: string,
): void => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const record = reason as { error?: unknown; meta?: unknown };
    const resolvedEndpoint =
      endpoint ??
      (typeof reason === 'object' && reason !== null
        ? endpointByRejection.get(reason)
        : undefined) ??
      null;
    const httpStatus = statusOf(record.error);
    const key = `${source}|${resolvedEndpoint}|${httpStatus}`;
    if (seen.has(key) || seen.size >= MAX_DISTINCT_PER_PAGE_LOAD) {
      return;
    }
    seen.add(key);
    pending.push({
      source,
      endpoint: resolvedEndpoint,
      http_status: httpStatus,
      error_keys: keysOf(record.error),
      meta_keys: keysOf(record.meta),
      route: normalizeApiPathForSpanName(window.location.pathname),
      visibility: document.visibilityState,
    });
    // A new entry restarts the retry budget so a late PostHog init still
    // flushes it.
    retries = 0;
    if (retryTimer === undefined) {
      flush();
    }
  } catch {
    // Diagnostics must never turn a swallowed rejection into a new one.
  }
};
